/* eslint-disable prettier/prettier */
import React, { useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import { useCurrenciesSlice } from './slice';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import {
  SquarePen,
  Trash,
  Save,
  X,
  RefreshCcw,
  RotateCw,
  Plus,
  Search,
  Globe,
  TrendingUp,
  Clock,
} from 'lucide-react';
import { AddCurrencyModal } from './components/add-modal';
import { DeleteDialog } from './components/delete';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { toast } from 'sonner';
import { convertDateFormat, formatDate } from 'utils/format-date';
import { Badge } from 'app/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import { useToast } from 'app/components/ui/use-toast';

export default function Currencies() {
  const [open, setOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('all');
  const { toast } = useToast();

  const {
    useGetCurrenciesQuery,
    useUpdateCurrencyMutation,
    useGetLatestExchangeRateQuery,
  } = useCurrenciesSlice();
  const [getCurrency, { data: currencies, error, isLoading }] =
    useGetCurrenciesQuery();
  const [updateCurrency, { data: updatedData, error: updateError }] =
    useUpdateCurrencyMutation();
  const [
    getLatestExchangeRate,
    { data: updatedRates, isLoading: loadingMarketRates, error: updatesError },
  ] = useGetLatestExchangeRateQuery();
  const [selectedCurrency, setSelectedCurrency] = React.useState({});
  const [editingRow, setEditingRow] = React.useState<number | null>(null);
  const [editValues, setEditValues] = React.useState({});

  useEffect(() => {
    getCurrency({});
  }, []);

  const handleEdit = (id: number, field: string, value: any) => {
    setEditingRow(id);
    setEditValues(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleSave = (id: number) => {
    const originalData = currencies?.data.find((c: any) => c.id === id) || {};
    const updatedData = editValues[id] || {};

    const payload = {
      ...originalData,
      ...updatedData,
      status: Number(updatedData.status || originalData.status),
    };

    updateCurrency(payload);

    setEditingRow(null);
    setEditValues(prev => ({
      ...prev,
      [id]: undefined,
    }));
  };

  useEffect(() => {
    if (updatedRates) {
      toast({
        title: 'Exchange rates updated',
        description: 'Exchange rates have been updated successfully',
        variant: 'success',
      });
    }
  }, [updatesError]);

  useEffect(() => {
    if (updateError) {
      const errorMessage =
        (updateError as any)?.data?.message || 'Update failed';
      toast({
        title: 'Update failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
    if (updatedData) {
      toast({
        title: 'Update successful',
        description: updatedData?.message,
        variant: 'success',
      });
    }
    if (updatedRates) {
      toast({
        title: 'Exchange rates updated',
        description: updatedRates?.message,
        variant: 'success',
      });
      getCurrency({});
    }
    if (updatesError) {
      const errorMessage =
        (updatesError as any)?.data?.message || 'Exchange rate update failed';
      toast({
        title: 'Exchange rate update failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  }, [updateError, updatedData, updatedRates, updatesError]);

  return (
    <div className="space-y-6">
      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle className="mb-1">Currency Management</CardTitle>
          <CardDescription>
            Search, filter, and manage your currencies
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center gap-5 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search currencies..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex items-center gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Currencies</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              <Button
                onClick={() => getLatestExchangeRate({})}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white"
                disabled={loadingMarketRates}
              >
                <RefreshCcw className="w-4 h-4" />
                Refresh Rates
              </Button>

              <Button
                onClick={() => setOpen(true)}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Currency
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Last updated:{' '}
              {currencies?.data && currencies.data.length > 0
                ? convertDateFormat(
                    Math.max(
                      ...currencies.data.map((c: any) =>
                        new Date(c.updated_at).getTime(),
                      ),
                    ),
                  )
                : 'N/A'}
            </span>
          </div>

          <div className="rounded-md border overflow-hidden mt-5">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Currency</TableHead>
                  <TableHead>Currency Symbol</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Live Rate</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currencies?.data?.map((currency: any) => (
                  <TableRow key={currency.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {currency.currency_code}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {currency.country_name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {editingRow === currency.id ? (
                        <Input
                          value={
                            editValues[currency.id]?.currency_symbol ??
                            currency.currency_symbol
                          }
                          onChange={e =>
                            handleEdit(
                              currency.id,
                              'currency_symbol',
                              e.target.value,
                            )
                          }
                          className="w-20 h-8"
                        />
                      ) : (
                        <span className="text-sm">
                          {currency.currency_symbol || '-'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingRow === currency.id ? (
                        <Input
                          type="number"
                          value={editValues[currency.id]?.rate || currency.rate}
                          onChange={e =>
                            handleEdit(currency.id, 'rate', e.target.value)
                          }
                          className="w-24 h-8"
                        />
                      ) : (
                        <span className="font-mono">{currency.rate}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">
                        {currency?.exchange_rate}
                      </span>
                    </TableCell>
                    <TableCell>
                      {currency?.is_default ? (
                        <Badge
                          variant="default"
                          className="bg-green-100 text-green-800 hover:bg-green-100"
                        >
                          Default
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">
                          No
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingRow === currency.id ? (
                        <Select
                          value={
                            editValues[currency.id]?.status ??
                            String(currency.status)
                          }
                          onValueChange={val =>
                            handleEdit(currency.id, 'status', val)
                          }
                          disabled={isLoading || currency.is_default}
                        >
                          <SelectTrigger className="w-24 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Active</SelectItem>
                            <SelectItem value="0">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge
                          variant={
                            currency.status === 1 ? 'default' : 'secondary'
                          }
                          className={
                            currency.status === 1
                              ? 'bg-green-100 text-green-800 hover:bg-green-100'
                              : ''
                          }
                        >
                          {currency.status === 1 ? 'Active' : 'Inactive'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {editingRow === currency.id ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                              onClick={() => handleSave(currency.id)}
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                              onClick={() => {
                                setEditingRow(null);
                                setEditValues(prev => ({
                                  ...prev,
                                  [currency.id]: undefined,
                                }));
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => setEditingRow(currency.id)}
                              disabled={currency.is_default}
                            >
                              <SquarePen className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                              onClick={() => {
                                setSelectedCurrency(currency);
                                setDeleteOpen(true);
                              }}
                              disabled={currency.is_default}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <AddCurrencyModal open={open} onOpenChange={setOpen} />
      <DeleteDialog
        isOpen={deleteOpen}
        onOpenChange={setDeleteOpen}
        data={selectedCurrency}
      />
    </div>
  );
}
